import type { UseMutationResult } from "@tanstack/react-query"
import { useMutation, useQueryClient } from "@tanstack/react-query"

import { queryKeys } from "~/api/cache"

import type { BatteryProposalFinancingDto } from "../dto/battery-proposal.dto"
import { batteryProposalService } from "../services/battery-proposal.service"

export type BatteryLightReachContractCommand =
  | { kind: `refresh` }
  | { kind: `retrySupplemental`; attemptId: string }
  | { kind: `send` }

const run = (
  proposalId: string,
  command: BatteryLightReachContractCommand,
): Promise<BatteryProposalFinancingDto> => {
  switch (command.kind) {
    case `send`:
      return batteryProposalService.sendLightReachContract(proposalId)
    case `refresh`:
      return batteryProposalService.refreshLightReachContract(proposalId)
    case `retrySupplemental`:
      return batteryProposalService.retryLightReachSupplemental(
        proposalId,
        command.attemptId,
      )
  }
}

/**
 * Send, refresh, or retry supplementals for the LightReach contract. Each
 * route returns the financing read. A failure may still have reached
 * LightReach, so it re-reads financing too.
 */
export default function useBatteryLightReachContract(): UseMutationResult<
  BatteryProposalFinancingDto,
  unknown,
  { proposalId: string; command: BatteryLightReachContractCommand }
> {
  const queryClient = useQueryClient()

  // The proposal read carries the Contract entry, which a contract changes.
  const invalidateProposal = (proposalId: string): void => {
    void queryClient.invalidateQueries({
      queryKey: queryKeys.batteryProposal.byId(proposalId),
      exact: true,
    })
  }

  return useMutation({
    mutationFn: ({ proposalId, command }) => run(proposalId, command),
    // A financing read already in flight (Refresh status) must not land after
    // this response and bring back the pre-send actions.
    onMutate: ({ proposalId }) =>
      queryClient.cancelQueries({
        queryKey: queryKeys.batteryProposal.financing(proposalId),
      }),
    onSuccess: async (financing, { proposalId }) => {
      const financingKey = queryKeys.batteryProposal.financing(proposalId)
      // A Refresh status started while the command ran read pre-command state.
      await queryClient.cancelQueries({ queryKey: financingKey })
      queryClient.setQueryData(financingKey, financing)
      invalidateProposal(proposalId)
    },
    onError: (_error, { proposalId }) => {
      void queryClient.invalidateQueries({
        queryKey: queryKeys.batteryProposal.financing(proposalId),
      })
      invalidateProposal(proposalId)
    },
  })
}
